import { useEffect, useState, useContext, useMemo } from "react";
import { useEffectOnce } from "../hooks/useEffectOnce";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useRetryFetch from "../hooks/useRetryFetch";
import { tryGetUserRoles } from "../api/auth";
import { useTranslation } from "react-i18next";
import { config } from "../Environment";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import "./PrescriptionList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { distributionService } from "../services/distributionService";
import cookies from "js-cookie";

import LoggerContext from "../context/LoggerProvider";
import PresListContext from "../context/PresListProvider";
import { WSContext } from "../App";

// for modal Preview!
import Preview from "./Preview";

import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "./ui/dropdown-menu";

import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "./ui/menubar";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { LocationState, User } from "../lib/types";
import { Import } from "lucide-react";

import WorkflowPermissionDialog from "./WorkflowPermissionDialog";
import LinkClientExpertTable from "./LinkClientExpertTable";
import MessageModal from "./MessageModal";

interface UserRoleListProps {
  roles: string[]
}

function UserRoleList({ roles = [] }: UserRoleListProps) {
  const listItems = [];
  for(let i = 0; i < roles.length; i += 1) {
    listItems.push(roles[i]);
  }
  return <span>{listItems.join(", ")}</span>
}

const Admin = () => {
  const { auth, setAuth } = useAuth();

  const {
    refreshLists,
    prescriptions,
    publicPrescriptions,
    processes,
    expertProcesses,
  } = useContext(PresListContext);

  const location = useLocation();
  const navigate = useNavigate();
  // background shows that PREVIEW should be opened as modal on top of current page!
  let background = null;
  if (location.state && typeof location.state === "object") {
    const state = location.state as LocationState;
    if (state.background && typeof state.background === "object") {
      if ("pathname" in state.background) {
        background =
          state.background.pathname !== "/" ? state.background : null;
      }
    }
  }

  const { t } = useTranslation();

  const [currentPrescription, setCurrentPrescription] = useState("");
  const [currentProcess, setCurrentProcess] = useState("");
  const [allUsers, setAllUsers] = useState<User[]>([]);

  const [reference, setReference] = useState(null);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 800px)").matches
  );

  const [filterown, setFilterown] = useState("ALL");
  const [filterexpert, setFilterexpert] = useState("ALL");

  const [showTerminate, setShowTerminate] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showRename, setShowRename] = useState(false);

  const [presNameNew, setPresNameNew] = useState("");

  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [messageClient, setMessageClient] = useState<any | null>(null);

  const [errMsg, setErrMsg] = useState("");

  const validPrescriptions = useMemo(() => {
    return prescriptions?.filter((prescription: any) => !!prescription.valid);
  }, [prescriptions]);

  const filteredProcesses = useMemo(() => {
    return filterown === "ALL"
      ? processes
      : processes?.filter((process: any) => process.status === filterown);
  }, [processes, filterown]);

  const filteredExpertProcesses = useMemo(() => {
    return filterexpert === "ALL"
      ? expertProcesses
      : expertProcesses?.filter(
          (process: any) => process.status === filterexpert
        );
  }, [expertProcesses, filterexpert]);

  const { Logger } = useContext(LoggerContext);

  const { listRefresh, setListRefresh, socket } = useContext(WSContext);

  const retryFetch = useRetryFetch();

  useEffectOnce(() => {
    const handler = (e: any) => setIsMobile(e.matches);
    window.matchMedia("(max-width: 800px)").addEventListener("change", handler);

    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("prescriptionHistoryID");
    sessionStorage.removeItem("runProcessID");

    refreshLists();

    // subscribe to incoming messages
    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe((msg) => {
        Logger.debug("PrescriptionList incoming message: ");
        Logger.debug(msg);

        switch (msg.command) {
        }
      });

    (async function () {
      let response = await retryFetch(`${config.API_BASE}/api/sysadmin/users`);
      let responseBody = await response.json();
      if (Array.isArray(responseBody.list)) {
        const users: User[] = responseBody.list;
        users.sort((a: any, b: any) => {
          if (a.email < b.email) return -1;
          if (a.email > b.email) return 1;
          return 0;
        });

        setAllUsers(users);
      }
    })();

    return () => {
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  const selectUser = (idx: any) => {
    navigate(`/users/${allUsers[idx]._id}`);
  };

  const impersonateUser = async (idx: any) => {
    let response = await retryFetch(
      `${config.API_BASE}/api/sysadmin/impersonate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          target_user_id: allUsers[idx]._id,
        }),
      }
    );

    let responseBody = await response.json();
    if (responseBody.accessToken) {
      const { email: impersonatorEmail } = auth;
      const { email } = allUsers[idx];
      const { accessToken } = responseBody;
      // load user's roles
      setAuth({
        email,
        accessToken,
        roles: undefined,
        impersonatedBy: impersonatorEmail,
      }); // accessToken must be set to call roles APi
      localStorage.setItem("auth", JSON.stringify({ email, accessToken }));
      const roles = await tryGetUserRoles(retryFetch);
      if (!Array.isArray(roles)) {
        setErrMsg(t("msg_error_failed_loading_user_roles"));
      }

      setAuth({ email, accessToken, roles, impersonatedBy: impersonatorEmail });
      console.log(`Admin.jsx storing auth ${email} with roles ${roles}`);
      localStorage.setItem(
        "auth",
        JSON.stringify({
          email,
          accessToken,
          roles,
          impersonatedBy: impersonatorEmail,
        })
      ); // TODO: add impersonatedBy field

      // send login message
      socket.send(
        JSON.stringify({
          command: "login",
          payload: {
            email,
          },
        })
      );

      setListRefresh(true);
      navigate(`/portal`);
    } else {
      // message that it failed, don't navigate anywhere
    }
  };

  const managePermissions = async (idx: any) => {
    /*
    let response = await retryFetch(
      `${config.API_BASE}/api/deeplinks/${prescriptions[idx]._id}`,
      {
        method: "POST",
      }
    );
    let newDeeplink = await response.json();
    if (window.isSecureContext) {
      navigator.clipboard.writeText(
        `${window.location.origin}/preview?id=${newDeeplink.id}`
      );
      toast.success(t("msg_link_copied_to_clipboard"));
    } else {
      toast.error(t("msg_error_clipboard_insecure"), {
        className: "!bg-red-500 !text-white",
      });
    }
      */
    //    refreshLists();
    setCurrentPrescription(prescriptions[idx]._id);
    setShowPermissionDialog(true);
  };

  const viewPrescription = (idx: any, isMine: boolean) => {
    if (isMine)
      sessionStorage.setItem("prescriptionID", prescriptions[idx]._id);
    else sessionStorage.setItem("prescriptionID", publicPrescriptions[idx]._id);

    sessionStorage.removeItem("draftprescription");
    navigate("/readonly");
  };

  useEffect(() => {
    if (listRefresh) {
      refreshLists();
      setListRefresh(false);
    }
  }, [listRefresh]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Track sidebar state

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen); // Toggle the sidebar state
  };

  const handleTabClick = () => {
    setIsSidebarOpen(false); // Close sidebar when a tab is clicked
  };

  return (
    <div>
      {errMsg ? <div className="error">{errMsg}</div> : ""}
      {isMobile ? (
        <>
          <button
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            style={{
              position: "absolute",
              top: "70px",
              left: "20px",
              zIndex: 10,
            }}
          >
            <FontAwesomeIcon icon={solid("bars")} size="lg" />
          </button>
          <Tabs className="mt-5">
            <div
              className={`sidebar ${isSidebarOpen ? "open" : ""}`}
              style={{
                position: "fixed",
                top: "0",
                left: "0",
                height: "100vh",
                width: "250px",
                backgroundColor: "#333",
                color: "white",
                display: isSidebarOpen ? "block" : "none",
                transition: "transform 0.3s ease",
                transform: isSidebarOpen
                  ? "translateX(0)"
                  : "translateX(-100%)",
              }}
            >
              <div className="sidebar-content mt-5">
                {/* <Tabs className=""> */}
                <TabsList
                  className="w-full py-2 px-5 flex flex-col gap-2"
                  style={{ height: "100%", paddingTop: "40px" }} // Ensures full-height sidebar
                >
                  <TabsTrigger
                    className="whitespace-nowrap px-4 py-2 text-sm sm:text-base rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow"
                    value="user_list_tab"
                    onClick={handleTabClick}
                  >
                    {t("user_list")}
                  </TabsTrigger>
                  <TabsTrigger
                    className="whitespace-nowrap px-4 py-2 text-sm sm:text-base rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow"
                    value="public"
                    disabled={!auth.email}
                    onClick={handleTabClick}
                  >
                    {t("other_activities")}
                  </TabsTrigger>
                </TabsList>
                {/* </Tabs> */}
              </div>
            </div>
            <TabsContent className="mt-14" value="user_list_tab">
              {/* MOBILE VIEW */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("first_name")}</TableHead>
                    <TableHead>{t("last_name")}</TableHead>
                    <TableHead className="text-start">
                      {t("email_address")}
                    </TableHead>
                    <TableHead>{t("roles")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!allUsers
                    ? allUsers.map((user: any, index: any) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>{user.firstName}</TableCell>
                            <TableCell>{user.lastName}</TableCell>
                            <TableCell className="text-start">
                              {user.email}
                            </TableCell>
                            <TableCell><UserRoleList roles={user.roles}/></TableCell>
                            <TableCell className="equallySpaced">
                              <DropdownMenu>
                                <DropdownMenuTrigger className="bg-transparent ms-2">
                                  <div className="w-[30px]">
                                    <FontAwesomeIcon
                                      title={t("actions")}
                                      style={{ paddingTop: "4px" }}
                                      icon={solid("ellipsis-vertical")}
                                    />
                                  </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => selectUser(index)}
                                  >
                                    <div className="flex">
                                      <FontAwesomeIcon
                                        title={t("edit")}
                                        icon={solid("pen")}
                                        className="ml-2 mr-2 mt-1 w-[15px]"
                                      />
                                      <span>{t("edit")}</span>
                                    </div>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => impersonateUser(index)}
                                  >
                                    <div className="flex">
                                      <FontAwesomeIcon
                                        title={t("impersonate")}
                                        icon={solid("eye")}
                                        className="ml-2 mr-2 mt-1 w-[15px]"
                                      />
                                      <span>{t("impersonate")}</span>
                                    </div>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>
            <TabsContent className="mt-14" value="public">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!publicPrescriptions
                    ? publicPrescriptions.map(
                        (prescription: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                className={`${
                                  (prescription.isWelcome ? "font-bold" : "") +
                                  (!prescription.valid ? " text-gray-300" : "")
                                }`}
                              >
                                {prescription.name}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        viewPrescription(index, false)
                                      }
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("view")}
                                          icon={solid("eye")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("view")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <div className="preslist centercontainer">
          <Tabs defaultValue="user_list_tab">
            <TabsList>
              <TabsTrigger
                className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                value="user_list_tab"
              >
                {t("user_list")}
              </TabsTrigger>
              <TabsTrigger
                className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                value="public"
                disabled={!auth.email}
              >
                {t("other_activities")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="user_list_tab">
              {/* DESKTOP VIEW */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("first_name")}</TableHead>
                    <TableHead>{t("last_name")}</TableHead>
                    <TableHead className="text-start">
                      {t("email_address")}
                    </TableHead>
                    <TableHead className="text-start">{t("roles")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!allUsers
                    ? allUsers.map((user: any, index: any) => {
                        return (
                          <TableRow key={index} data-userid={user._id}>
                            <TableCell>{user.firstName}</TableCell>
                            <TableCell>{user.lastName}</TableCell>
                            <TableCell className="text-start">
                              {user.email}
                            </TableCell>
                            <TableCell className="text-start">
                              <UserRoleList roles={user.roles}/>
                            </TableCell>
                            <TableCell className="equallySpaced">
                              <DropdownMenu>
                                <DropdownMenuTrigger className="bg-transparent ms-2">
                                  <div className="w-[30px]">
                                    <FontAwesomeIcon
                                      title={t("actions")}
                                      style={{ paddingTop: "4px" }}
                                      icon={solid("ellipsis-vertical")}
                                    />
                                  </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => selectUser(index)}
                                  >
                                    <div className="flex">
                                      <FontAwesomeIcon
                                        title={t("edit")}
                                        icon={solid("pen")}
                                        className="ml-2 mr-2 mt-1 w-[15px]"
                                      />
                                      <span>{t("edit")}</span>
                                    </div>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => impersonateUser(index)}
                                  >
                                    <div className="flex">
                                      <FontAwesomeIcon
                                        title={t("impersonate")}
                                        icon={solid("eye")}
                                        className="ml-2 mr-2 mt-1 w-[15px]"
                                      />
                                      <span>{t("impersonate")}</span>
                                    </div>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>
            <TabsContent value="public">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!publicPrescriptions
                    ? publicPrescriptions.map(
                        (prescription: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                className={`${
                                  (prescription.isWelcome ? "font-bold" : "") +
                                  (!prescription.valid ? " text-gray-300" : "")
                                }`}
                              >
                                {prescription.name}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        viewPrescription(index, false)
                                      }
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("view")}
                                          icon={solid("eye")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("view")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </div>
      )}
      <Dialog open={showTerminate} onOpenChange={setShowTerminate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("terminate_engagement")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_terminate")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                Logger.debug("Current engagement: " + currentProcess);
                let response = await retryFetch(
                  `${config.API_BASE}/api/processes/${currentProcess}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  }
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentProcess("");
                setShowTerminate(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowTerminate(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showArchive} onOpenChange={setShowArchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("archive_activity")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_archive_activity")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                let response = await retryFetch(
                  `${config.API_BASE}/api/prescriptions/${currentPrescription}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  }
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentPrescription("");
                setShowArchive(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowArchive(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("delete_activity")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_delete_activity")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                let response = await retryFetch(
                  `${config.API_BASE}/api/prescriptions/${currentPrescription}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  }
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentPrescription("");
                setShowDelete(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowDelete(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("msg_rename_activity_to")}</DialogTitle>
          </DialogHeader>
          <label htmlFor="newName">{t("name")}</label>
          <input
            className="border-2 rounded pt-1 pl-2 pb-1"
            type="text"
            value={presNameNew}
            onChange={(e) => setPresNameNew(e.target.value)}
            id="newName"
          />
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                await retryFetch(
                  `${config.API_BASE}/api/prescriptions/rename/${reference}/${presNameNew}`,
                  {
                    method: "PUT",
                    credentials: "include",
                  }
                );
                setReference(null);
                setShowRename(false);
                setPresNameNew("");
                setListRefresh(true);
                // toast.success(t("msg_activity_renamed")); TODO: fix when intrdoucing react query
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
              disabled={!presNameNew}
            >
              {t("ok")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowRename(false);
                setPresNameNew("");
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {messageClient && (
        <MessageModal
          open={!!messageClient}
          clientName={messageClient.clientName}
          onClose={() => setMessageClient(null)}
        />
      )}
      <WorkflowPermissionDialog
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
        workflowId={currentPrescription}
      ></WorkflowPermissionDialog>
      {background && <Preview />}
    </div>
  );
};

export default Admin;
